import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface GuardianChild {
  playerId: string;
  playerName: string;
  teamId: string;
  teamName: string;
  status: "active" | "pending";
}

export interface ParentDashboardPayload {
  userEmail: string | null;
  children: GuardianChild[];
  games: any[];
  rsvps: any[];
  unavailability: any[];
  coParents: Record<string, { email: string; status: "active" | "pending" }[]>;
}

interface GuardianStatus {
  isGuardian: boolean;
  children: GuardianChild[];
  activeChildren: GuardianChild[];
  pendingChildren: GuardianChild[];
  loading: boolean;
  userId: string | null;
  userEmail: string | null;
  payload: ParentDashboardPayload | null;
}

interface GuardianStatusResult extends GuardianStatus {
  refetch: () => void;
}

const REFETCH_DEBOUNCE_MS = 5000;

export function useGuardianStatus(): GuardianStatusResult {
  const [children, setChildren] = useState<GuardianChild[]>([]);
  const [hasGuardianRecords, setHasGuardianRecords] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [payload, setPayload] = useState<ParentDashboardPayload | null>(null);
  const lastFetchRef = useRef(0);

  const fetchGuardianStatus = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setChildren([]);
        setUserId(null);
        setUserEmail(null);
        setPayload(null);
        setLoading(false);
        return;
      }
      setUserId(session.user.id);
      setUserEmail(session.user.email || null);

      const { data, error } = await supabase.rpc("get_parent_dashboard", {
        p_user_id: session.user.id,
      });

      if (error || !data) {
        if (error) console.error("Error fetching parent dashboard:", error);
        setChildren([]);
        setPayload(null);
        setLoading(false);
        return;
      }

      const p = data as unknown as ParentDashboardPayload;
      setPayload(p);
      setHasGuardianRecords((p.children?.length || 0) > 0);
      setChildren(p.children || []);
      if (p.userEmail) setUserEmail(p.userEmail);
    } catch (err) {
      console.error("Error in useGuardianStatus:", err);
    } finally {
      setLoading(false);
      lastFetchRef.current = Date.now();
    }
  }, []);

  const debouncedRefetch = useCallback(() => {
    if (Date.now() - lastFetchRef.current < REFETCH_DEBOUNCE_MS) return;
    fetchGuardianStatus();
  }, [fetchGuardianStatus]);

  useEffect(() => {
    let cancelled = false;
    const wrappedFetch = () => {
      if (!cancelled) fetchGuardianStatus();
    };

    wrappedFetch();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      wrappedFetch();
    });

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') debouncedRefetch();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', debouncedRefetch);

    return () => {
      cancelled = true;
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', debouncedRefetch);
    };
  }, [fetchGuardianStatus, debouncedRefetch]);

  const activeChildren = useMemo(() => children.filter(c => c.status === "active"), [children]);
  const pendingChildren = useMemo(() => children.filter(c => c.status === "pending"), [children]);

  return {
    isGuardian: hasGuardianRecords,
    children,
    activeChildren,
    pendingChildren,
    loading,
    userId,
    userEmail,
    payload,
    refetch: fetchGuardianStatus,
  };
}
